import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOptimizedAuth } from '@/hooks/useOptimizedAuth';
import { useToast } from '@/hooks/use-toast';
import type { AdvancedNotification, NotificationType, NotificationPriority } from '@/types/notifications';

interface NotificationConnection {
  isConnected: boolean;
  reconnectAttempts: number;
  lastReconnectTime: number;
}

export const useMasterNotifications = () => {
  const { user, isSuperAdmin, hasAdminAccess } = useOptimizedAuth();
  const { toast } = useToast();
  
  const [notifications, setNotifications] = useState<AdvancedNotification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<NotificationConnection>({
    isConnected: false,
    reconnectAttempts: 0,
    lastReconnectTime: 0
  });

  // Fetch notifications from database
  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      // First verify authentication context
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        console.warn('No valid session for notifications fetch');
        setNotifications([]);
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        console.warn('Error fetching notifications:', error.message);
        if (error.message.includes('JWT') || error.message.includes('auth')) {
          console.warn('Authentication error detected, retrying in 1 second...');
          setTimeout(() => fetchNotifications(), 1000);
          return;
        }
        setNotifications([]);
        return;
      }

      // Transform database notifications to AdvancedNotification format
      const transformedNotifications: AdvancedNotification[] = (data || []).map(notification => ({
        id: notification.id,
        type: notification.type as NotificationType,
        title: notification.title,
        message: notification.message,
        priority: mapPriorityToNumber(notification.priority || 'medium'),
        timestamp: new Date(notification.created_at).getTime(),
        metadata: (notification.metadata as Record<string, any>) || {},
        persistent: notification.priority === 'urgent'
      }));

      setNotifications(transformedNotifications);
    } catch (error) {
      console.warn('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Helper function to map priority strings to numbers
  const mapPriorityToNumber = (priority: string): number => {
    switch (priority) {
      case 'urgent': return 4;
      case 'high': return 3;
      case 'medium': return 2;
      case 'low': return 1;
      default: return 2;
    }
  };

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ 
          is_read: true, 
          read_at: new Date().toISOString() 
        })
        .eq('id', notificationId);

      if (error) {
        console.error('Error marking notification as read:', error);
        return;
      }

      // Update local state - no need to update since we don't track read status in AdvancedNotification
      // The database update is sufficient for the notification list
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) {
        console.error('Error deleting notification:', error);
        return;
      }

      // Update local state
      setNotifications(prev => prev.filter(notification => notification.id !== notificationId));
      toast({
        title: "Notification deleted",
        description: "The notification has been successfully deleted."
      });
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  }, [toast]);

  // Create notification (admin only)
  const createNotification = useCallback(async (
    title: string,
    message: string,
    type: string = 'system_error',
    priority: NotificationPriority = 'medium',
    userId?: string,
    metadata?: Record<string, any>
  ) => {
    if (!hasAdminAccess && !isSuperAdmin) {
      throw new Error('Insufficient permissions to create notifications');
    }

    try {
      const { data, error } = await supabase
        .from('notifications')
        .insert({
          type: type as any,
          title,
          message,
          priority,
          user_id: userId || null,
          metadata: metadata || {}
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating notification:', error);
        throw error;
      }

      toast({
        title: "Notification created",
        description: "The notification has been successfully created."
      });

      return data;
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  }, [hasAdminAccess, isSuperAdmin, toast]);

  const getUnreadCount = useCallback(() => {
    return 0; // Read status tracking will be implemented when needed
  }, [notifications]);

  // Set up real-time subscriptions
  useEffect(() => {
    if (!user) return;

    // Setting up master notification subscription

    const channel = supabase
      .channel('master-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: (isSuperAdmin || hasAdminAccess) ? undefined : `user_id=eq.${user.id}`
        },
        (payload) => {
          // New notification received
          
          const newNotification: AdvancedNotification = {
            id: payload.new.id,
            type: payload.new.type as NotificationType,
            title: payload.new.title,
            message: payload.new.message,
            priority: mapPriorityToNumber(payload.new.priority || 'medium'),
            timestamp: new Date(payload.new.created_at).getTime(),
            metadata: payload.new.metadata || {},
            persistent: payload.new.priority === 'urgent'
          };

          setNotifications(prev => [newNotification, ...prev]);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: (isSuperAdmin || hasAdminAccess) ? undefined : `user_id=eq.${user.id}`
        },
        (payload) => {
          // Notification updated
          
           setNotifications(prev => prev.map(notification => 
             notification.id === payload.new.id 
               ? {
                   ...notification,
                   title: payload.new.title,
                   message: payload.new.message
                 }
               : notification
           ));
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'notifications',
          filter: (isSuperAdmin || hasAdminAccess) ? undefined : `user_id=eq.${user.id}`
        },
        (payload) => {
          // Notification deleted
          
          setNotifications(prev => prev.filter(notification => notification.id !== payload.old.id));
        }
      )
      .subscribe((status) => {
        // Master notification subscription status
        
        if (status === 'SUBSCRIBED') {
          setConnectionStatus(prev => ({
            ...prev,
            isConnected: true,
            reconnectAttempts: 0
          }));
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          setConnectionStatus(prev => ({
            ...prev,
            isConnected: false
          }));
        }
      });

    // Initial fetch
    fetchNotifications();

    return () => {
      // Cleaning up master notification subscription
      supabase.removeChannel(channel);
    };
  }, [user?.id, isSuperAdmin, hasAdminAccess, fetchNotifications, toast]);

  return {
    notifications,
    isLoading,
    connectionStatus,
    unreadCount: getUnreadCount(),
    markAsRead,
    deleteNotification,
    createNotification,
    refetch: fetchNotifications
  };
};